/** @file commandMethods: command batches, the revision log, undo, redo and template instantiation. */
import type { BatchReply } from '@zilo/pose3d-commands/types/batch';
import type { CommandInput } from '@zilo/pose3d-commands/types/command';
import type { CommandsBody } from '../types/rest';
import type { RevisionsReply } from '../types/restAdded';
import type { ClientOptions } from './clientOptions';
import { fillPath } from './fillPath';
import { request } from './request';

/** A batch reply plus the document hash after it (header `X-Pose3D-Doc-Hash`; empty when absent). */
export type CommittedReply = BatchReply & { docHash: string };

/** The command members of the client. */
export type CommandMethods = {
  /** `POST /api/v1/puppets/{id}/commands`; failed commands come back as `ok: false` replies, not throws. */
  commands: (puppetId: string, body: CommandsBody) => Promise<CommittedReply>;
  /** `GET /api/v1/puppets/{id}/revisions`: the rows (kind, label, targets) and the undo/redo `history`. */
  revisions: (puppetId: string, q?: { from?: number; limit?: number }) => Promise<RevisionsReply>;
  /** `POST /api/v1/puppets/{id}/undo`: undoes the last `count` batches (server default 1). */
  undo: (puppetId: string, count?: number) => Promise<BatchReply>;
  /** `POST /api/v1/puppets/{id}/redo`: re-applies the newest `count` undos (server default 1); `REDO_STALE` after a batch. */
  redo: (puppetId: string, count?: number) => Promise<BatchReply>;
  /** `commands` with the one command `template.instantiate` on an existing puppet (`scope` narrows the template, CR-002). */
  templateInstantiate: (puppetId: string, input: CommandInput['template.instantiate']) => Promise<CommittedReply>;
};

const PUPPET = '/api/v1/puppets/{id}';

/** Command calls over `opts`. */
export function commandMethods(opts: ClientOptions): CommandMethods {
  const commands = async (id: string, body: CommandsBody): Promise<CommittedReply> => {
    const r = await request<BatchReply>(opts, { method: 'POST', path: fillPath(`${PUPPET}/commands`, { id }), body, accept: 'json' });
    return { ...r.value, docHash: r.headers.get('x-pose3d-doc-hash') ?? '' };
  };
  const step = async (id: string, verb: 'undo' | 'redo', count?: number): Promise<BatchReply> => (await request<BatchReply>(opts, {
    method: 'POST', path: fillPath(`${PUPPET}/${verb}`, { id }), body: count === undefined ? {} : { count }, accept: 'json',
  })).value;
  return {
    commands,
    revisions: async (id, q = {}) => (await request<RevisionsReply>(opts, {
      method: 'GET', path: fillPath(`${PUPPET}/revisions`, { id }), query: { from: q.from, limit: q.limit }, accept: 'json',
    })).value,
    undo: (id, count) => step(id, 'undo', count),
    redo: (id, count) => step(id, 'redo', count),
    templateInstantiate: (id, input) => commands(id, { commands: [{ name: 'template.instantiate', input }] }),
  };
}
